<template>
  <div class="date-picker-row">
    <template v-for="(date, index) in completeDate">
      <div
        ref="date-picker-day-container"
        :key="`date-picker-day-${index}`"
        :data-date="date"
        :class="getCellContainerClass(index, date)"
        @mouseover="!checkDisabledDate(index, date) ? $emit('hovered-date', new Date(getPickerYear, getPickerMonth, date)) : null"
        @mouseleave="!checkDisabledDate(index, date) ? $emit('hovered-date', null) : null"
      >
        <div
          :class="getCellClass(index, date)"
          @click="e => handleDateClick(e, { index, date })"
        >
          {{ getDateCopy(index, date) }}
        </div>
      </div>
      <div
        v-if="(index + 1) % 7 === 0"
        :key="`date-picker-day-break-${(index + 1) / 7}`"
        class="date-picker-break"
      />
    </template>
    <div
      v-if="!isRangedDatePicker"
      class="date-picker-shortcut-button"
      @click="setToday"
    >
      {{ getShortcutCopy('day') }}
    </div>
  </div>
</template>

<script>
import DatePickerMixins from '../mixins';

export default {
  name: 'MDatePickerDayPicker',
  mixins: [
    DatePickerMixins,
  ],
  props: {
    previousDateList: {
      type: Array,
      default: () => [],
    },
    nextDateList: {
      type: Array,
      default: () => [],
    },
    currentDateList: {
      type: Array,
      default: () => [],
    },
    selectedDate: {
      type: [Number, Array],
      default: 1,
    },
    selectedMonth: {
      type: [Number, Array],
      default: 0,
    },
    selectedYear: {
      type: [Number, Array],
      default: 2020,
    },
    pickerDate: {
      type: [Number, Array],
      default: 1,
    },
    pickerMonth: {
      type: [Number, Array],
      default: 0,
    },
    pickerYear: {
      type: [Number, Array],
      default: 2020,
    },
    language: {
      type: String,
      default: 'en',
    },
    disabledDates: {
      type: Object, 
      default: null,
    },
    isRangedDatePicker: {
      type: Boolean,
      default: false,
    },
    idDayPicker: {
      type: Number,
      default: 0,
    },
    hoveredDate: {
      type: Date,
      default: null,
    },
  },
  data: () => ({
    completeDate: [],
    previousMonthIndex: -1,
    nextMonthIndex: -1,
  }),
  watch: {
    hoveredDate(date) {
      if (this.selectedDate[0] && this.selectedDate[1] === null) {
        this.setCellClass(date);
      }
    },
    previousDateList() {
      this.initDate();
    },
    currentDateList() {
      this.initDate();
    },
    nextDateList() {
      this.initDate();
    },
  },
  computed: {
    getSelectedDate() {
      return Array.isArray(this.selectedDate) ? this.selectedDate[this.idDayPicker] : this.selectedDate;
    },
    getSelectedMonth() {
      return Array.isArray(this.selectedMonth) ? this.selectedMonth[this.idDayPicker] : this.selectedMonth;
    },
    getSelectedYear() {
      return Array.isArray(this.selectedYear) ? this.selectedYear[this.idDayPicker] : this.selectedYear;
    },
    getPickerDate() {
      return Array.isArray(this.pickerDate) ? this.pickerDate[this.idDayPicker] : this.pickerDate;
    },
    getPickerMonth() {
      return Array.isArray(this.pickerMonth) ? this.pickerMonth[this.idDayPicker] : this.pickerMonth;
    },
    getPickerYear() {
      return Array.isArray(this.pickerYear) ? this.pickerYear[this.idDayPicker] : this.pickerYear;
    },
  },
  mounted() {
    this.initDate();
  },
  methods: {
    thisMonth(index) {
      return index > this.previousMonthIndex && index <= this.nextMonthIndex;
    },
    removeCellClass(el, selectedStartDate, currentCell, inFocusEndDate) {
      if (!(currentCell > selectedStartDate && currentCell < inFocusEndDate)) {
        el.classList.remove('date-picker-cell--full-colored');
      }
      if (!(currentCell === selectedStartDate && selectedStartDate !== inFocusEndDate)) {
        el.classList.remove('date-picker-cell--left-end');
      }
      if (!(currentCell === inFocusEndDate && selectedStartDate !== inFocusEndDate)) {
        el.classList.remove('date-picker-cell--right-end');
      }
    },
    addCellClass(el, reversingSelection, selectedStartDate, currentCell, inFocusEndDate) {
      if (reversingSelection) {
        if (currentCell < selectedStartDate && currentCell > inFocusEndDate) {
          el.classList.add('date-picker-cell--full-colored');
        } else if (currentCell === inFocusEndDate && selectedStartDate !== inFocusEndDate) {
          el.classList.add('date-picker-cell--left-end');
        } else if (currentCell === selectedStartDate && selectedStartDate !== inFocusEndDate) {
          el.classList.add('date-picker-cell--right-end');
        }
      } else if (currentCell > selectedStartDate && currentCell < inFocusEndDate) {
        el.classList.add('date-picker-cell--full-colored');
      } else if (currentCell === selectedStartDate && selectedStartDate !== inFocusEndDate) {
        el.classList.add('date-picker-cell--left-end');
      } else if (currentCell === inFocusEndDate && selectedStartDate !== inFocusEndDate) {
        el.classList.add('date-picker-cell--right-end');
      }
    },
    setCellClass(date) {
      const option = date ? 'add' : 'remove';
      const defaultEndDate = this.selectedDate[1]
        ? new Date(this.selectedYear[1], this.selectedMonth[1], this.selectedDate[1]).getTime()
        : new Date(this.selectedYear[0], this.selectedMonth[0], this.selectedDate[0]).getTime();
      const endDate = date
        ? date.getTime()
        : defaultEndDate;
      const selectedStartDate = new Date(this.selectedYear[0], this.selectedMonth[0], this.selectedDate[0]).getTime();
      const inFocusEndDate = endDate;
      let reversingSelection = false;
      if (inFocusEndDate <= selectedStartDate) {
        reversingSelection = true;
      }
      this.$refs['date-picker-day-container'].forEach((el, index) => {
        const currentDate = el.dataset.date;
        const currentIndex = index;
        const currentCell = new Date(this.getPickerYear, this.getPickerMonth, currentDate).getTime();
        if (this.thisMonth(currentIndex)) {
          if (option === 'add') {
            this.addCellClass(el, reversingSelection, selectedStartDate, currentCell, inFocusEndDate);
          } else {
            this.removeCellClass(el, selectedStartDate, currentCell, inFocusEndDate);
          }
        }
      });
    },
    getDateCopy(index, date) {
      if (this.isRangedDatePicker) {
        return !this.thisMonth(index) ? null : date;
      }
      return date;
    },
    setToday() {
      const date = new Date();
      const params = {
        date: date.getDate(),
        month: date.getMonth(),
        year: date.getFullYear(),
        from: 'datePicker',
      };
      this.$emit('update-selected-date', { ...params });
    },
    handleDateClick(event, { index, date }) {
      const classes = event.target.className;

      // emit date change event only if it's not disabled
      if (!classes.includes('disabled')) this.updateSelectedDate(index, date);
    },
    updateSelectedDate(index, date) {
      let month = this.getPickerMonth;
      let year = this.getPickerYear;
      if (index <= this.previousMonthIndex) {
        month -= 1;
      } else if (index > this.nextMonthIndex) {
        month += 1;
      }
      if (month < 0) {
        month = 11;
        year -= 1;
      } else if (month > 11) {
        month = 0;
        year += 1;
      }

      const params = {
        date,
        month,
        year,
        from: 'datePicker',
      };
      this.$emit('update-selected-date', { ...params });
    },
    getCellContainerClass(index, date) {
      const output = ['date-picker-cell'];
      if (this.isRangedDatePicker) {
        let monthOffset = 0;
        if (index <= this.previousMonthIndex) {
          monthOffset -= 1;
        }
        if (index > this.nextMonthIndex) {
          monthOffset += 1;
        }
        const selectedStartDate = new Date(this.selectedYear[0], this.selectedMonth[0], this.selectedDate[0]).getTime();
        const selectedEndDate = this.selectedDate[1]
          ? new Date(this.selectedYear[1], this.selectedMonth[1], this.selectedDate[1]).getTime()
          : new Date(this.selectedYear[0], this.selectedMonth[0], this.selectedDate[0]).getTime();
        const dateOnCell = new Date(this.getPickerYear, this.getPickerMonth + monthOffset, date).getTime();
        const additionalClasses = {
          'date-picker-cell--left-end': dateOnCell === selectedStartDate && this.thisMonth(index) && selectedStartDate !== selectedEndDate,
          'date-picker-cell--full-colored': dateOnCell > selectedStartDate && dateOnCell < selectedEndDate && this.thisMonth(index),
          'date-picker-cell--right-end': dateOnCell === selectedEndDate && this.thisMonth(index) && selectedStartDate !== selectedEndDate,
        };
        output.push(additionalClasses);
      }
      return output;
    },
    checkSelectedDate(date) {
      if (this.isRangedDatePicker) {
        const selectedStartDate = new Date(this.selectedYear[0], this.selectedMonth[0], this.selectedDate[0]).getTime();
        const selectedEndDate = new Date(this.selectedYear[1], this.selectedMonth[1], this.selectedDate[1]).getTime();
        const dateOnCell = new Date(this.getPickerYear, this.getPickerMonth, date).getTime();
        return dateOnCell === selectedStartDate || dateOnCell === selectedEndDate;
      } 
      return date === this.getSelectedDate && this.getPickerMonth === this.getSelectedMonth && this.getPickerYear === this.getSelectedYear;
      
    },
    checkDisabledDate(index, date) {
      if (this.isRangedDatePicker) {
        return this.isDisabledDate(date, index) || !this.thisMonth(index);
      }
      return this.isDisabledDate(date, index);
    },
    getCellClass(index, date) {
      const today = new Date();
      const isToday = today.getDate() === date && today.getMonth() === this.getPickerMonth && today.getFullYear() === this.getPickerYear;
      const isSelectedDate = this.checkSelectedDate(date);
      return {
        'date-picker-date-cell': true,
        'text-muted': !this.thisMonth(index),
        'current-date': this.thisMonth(index) && isToday,
        'selected-date': this.thisMonth(index) && isSelectedDate,
        'disabled-date': this.checkDisabledDate(index, date),
      };
    },
    initDate() {
      this.completeDate = [];
      this.completeDate.push(...this.previousDateList);
      this.previousMonthIndex = this.completeDate.length - 1;
      this.completeDate.push(...this.currentDateList);
      this.nextMonthIndex = this.completeDate.length - 1;
      this.completeDate.push(...this.nextDateList);
    },
    isDisabledDate(date, index) {
      let disabledDates = false;
      let month;
      let year = this.getPickerYear;

      if (!this.disabledDates) return false;

      // dates of last month must be disabled
      if (index <= this.previousMonthIndex) {
        month = this.getPickerMonth - 1;
        if (month < 0) {
          month = 11;
          year = this.getPickerYear - 1;
        }
      }
      else if (index > this.nextMonthIndex) {
        month = this.getPickerMonth + 1;
        if (month > 11) {
          month = 0;
          year = this.getPickerYear + 1;
        }
      }
      else month = this.getPickerMonth;
      
      // currentDate will be millisecond value / epoch time and time 00:00:00 of that date
      const currentDate = new Date(year, month, date);

      if (this.disabledDates.to && currentDate < this.disabledDates.to) {
        disabledDates = true;
      }

      if (this.disabledDates.from && currentDate >= this.disabledDates.from) {
        disabledDates = true;
      }

      return disabledDates;
    },
  },
};
</script>

<style lang="scss" scoped>
.date-picker {
  &-cell {
    &--right-end {
      background: linear-gradient(90deg, rgba(0, 95, 191, 0.1) 50%, #fff 50%);
    }
    &--left-end {
      background: linear-gradient(90deg, #fff 50%, rgba(0, 95, 191, 0.1) 50%);
    }
    &--full-colored {
      background: rgba(0, 95, 191, 0.1) ;
    }
  }
}
</style>
